import React, { createContext, useContext, useEffect, useReducer } from 'react'
import { authUtils, configureAmplify } from '@/utils/auth'
import { analyticsService } from '@/services/analytics'
import type { AuthContextType, AuthState, LoginCredentials, SignUpData, User } from '@/types/auth'

// Configure Amplify on module load
configureAmplify()

// Auth reducer for state management
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_ERROR' }
  | { type: 'LOGOUT' }

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
}

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false,
        error: null,
      }
    
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      }
    
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      }
    
    case 'LOGOUT':
      return {
        ...initialState,
        isLoading: false,
      }
    
    default:
      return state
  }
}

// Create context
const AuthContext = createContext<AuthContextType | null>(null)

// Auth provider component
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // Helper function for getting user ID for analytics (authenticated or anonymous)
  const getAnalyticsUserId = (currentUser?: User | null): string => {
    if (currentUser?.id) {
      return currentUser.id;
    }

    let anonymousId = localStorage.getItem('anonymous_user_id');
    if (!anonymousId) {
      anonymousId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      try {
        localStorage.setItem('anonymous_user_id', anonymousId);
      } catch (error) {
        console.warn('Failed to store anonymous user ID:', error);
      }
    }
    return anonymousId;
  };

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true })
        const user = await authUtils.getCurrentUser()
        dispatch({ type: 'SET_USER', payload: user })
      } catch (error) {
        dispatch({ type: 'SET_USER', payload: null })
      }
    }

    initializeAuth()
  }, [])

  // Auth methods
  const login = async (credentials: LoginCredentials) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      dispatch({ type: 'CLEAR_ERROR' })
      
      const user = await authUtils.signIn(credentials.email, credentials.password)
      dispatch({ type: 'SET_USER', payload: user })

      // Track successful login
      analyticsService.trackCustomEvent({
        event_type: 'authentication',
        event_data: {
          user_id: user.id,
          auth_action: 'login',
          auth_method: 'email',
          email_verified: user.email_verified
        }
      })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })

      // Track failed login attempt - use anonymous user ID for failed attempts
      analyticsService.trackCustomEvent({
        event_type: 'error',
        event_data: {
          user_id: getAnalyticsUserId(),
          error_type: 'auth_error',
          error_message: (error as Error).message,
          auth_method: 'email',
          auth_action: 'login'
        }
      })

      throw error
    }
  }

  const loginWithGoogle = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      dispatch({ type: 'CLEAR_ERROR' })

      await authUtils.signInWithGoogle()
      // Note: Google OAuth will redirect, so we don't set user here

      // Track Google login attempt - use anonymous ID since user not fully authenticated yet
      analyticsService.trackCustomEvent({
        event_type: 'authentication',
        event_data: {
          user_id: getAnalyticsUserId(),
          auth_action: 'login',
          auth_method: 'google'
        }
      })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })

      // Track failed Google login attempt
      analyticsService.trackCustomEvent({
        event_type: 'error',
        event_data: {
          user_id: getAnalyticsUserId(),
          error_type: 'auth_error',
          error_message: (error as Error).message,
          auth_method: 'google',
          auth_action: 'login'
        }
      })

      throw error
    }
  }

  const logout = async () => {
    try {
      // Track logout before clearing user data
      analyticsService.trackCustomEvent({
        event_type: 'authentication',
        event_data: {
          user_id: getAnalyticsUserId(state.user),
          auth_action: 'logout'
        }
      })

      await authUtils.signOut()
      dispatch({ type: 'LOGOUT' })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })

      // Track logout error
      analyticsService.trackCustomEvent({
        event_type: 'error',
        event_data: {
          user_id: getAnalyticsUserId(state.user),
          error_type: 'auth_error',
          error_message: (error as Error).message,
          auth_action: 'logout'
        }
      })

      throw error
    }
  }

  const signup = async (data: SignUpData) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      dispatch({ type: 'CLEAR_ERROR' })

      await authUtils.signUp(data.email, data.password, data.given_name, data.family_name)
      dispatch({ type: 'SET_LOADING', payload: false })

      // Track successful signup - use anonymous ID for signups before confirmation
      analyticsService.trackCustomEvent({
        event_type: 'authentication',
        event_data: {
          user_id: getAnalyticsUserId(),
          auth_action: 'signup',
          auth_method: 'email',
          email: data.email,
          given_name: data.given_name,
          family_name: data.family_name
        }
      })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })

      // Track signup error
      analyticsService.trackCustomEvent({
        event_type: 'error',
        event_data: {
          user_id: getAnalyticsUserId(),
          error_type: 'auth_error',
          error_message: (error as Error).message,
          auth_method: 'email',
          auth_action: 'signup'
        }
      })

      throw error
    }
  }

  const confirmSignUp = async (email: string, code: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      dispatch({ type: 'CLEAR_ERROR' })
      
      await authUtils.confirmSignUp(email, code)
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      throw error
    }
  }

  const resendConfirmationCode = async (email: string) => {
    try {
      dispatch({ type: 'CLEAR_ERROR' })
      await authUtils.resendConfirmationCode(email)
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      throw error
    }
  }

  const resetPassword = async (email: string) => {
    try {
      dispatch({ type: 'CLEAR_ERROR' })
      await authUtils.resetPassword(email)
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      throw error
    }
  }

  const confirmResetPassword = async (email: string, code: string, newPassword: string) => {
    try {
      dispatch({ type: 'CLEAR_ERROR' })
      await authUtils.confirmResetPassword(email, code, newPassword)
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      throw error
    }
  }

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' })
  }

  const contextValue: AuthContextType = {
    ...state,
    login,
    loginWithGoogle,
    logout,
    signup,
    confirmSignUp,
    resendConfirmationCode,
    resetPassword,
    confirmResetPassword,
    clearError,
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

// Custom hook to use auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}